import type { CommonDomainType, CubeId, ProjectId } from 'common/types'

import type { SurveyType } from './common'
import type { CubeInfo } from './cubes'
import type { FaultInfo, HorizonInfo, Survey2dInfo } from './seismicTypes'
import type { UploadId } from './types'
import type { WellListItem } from './wells'

type UploadedFileType = 'cube' | 'fault' | 'horizon' | 'survey2d' | 'unknown'
export type UploadedFileStatusType =
  | 'idle'
  | 'loading'
  | 'succeeded'
  | 'failed'
  | 'inprogress'
  | 'paused'
  | 'notstarted'
  | 'ready'
  | 'addedToProject'
  | 'excluded'
export type UploadedFileStepType = 'setup' | 'mapping' | 'loading' | 'conversion' | 'ready'
export type UploadedFileOriginType = 'localMine' | 'localAlien' | 'crms' | 'geodb'
export type UploadedFileEntityType = 'well' | 'segy' | 'marker'
export type UploadedFileStepTransitionType = UploadedFileStepType | 'next' | 'prev'

export type UploadedFile = {
  type?: UploadedFileType

  name: string

  content?: CubeInfo | FaultInfo | HorizonInfo | Survey2dInfo

  status: Exclude<UploadedFileStatusType, 'inprogress'>
  error?: string | undefined
}

export type MultipartFileStatusType = Exclude<UploadedFileStatusType, 'loading'>

export type MultipartFileStatusPerStepType = {
  [key in UploadedFileStepType]?: MultipartFileStatusType
}
export type MultipartUploadedFileSetupType = SegySetupType | MarkerSetupType
export type MultipartUploadedFile = {
  projectId: ProjectId
  fileId: string
  uploadId: UploadId | undefined
  name: string
  statuses?: MultipartFileStatusPerStepType
  error?: string
  progress: number
  fileSize: number
  fileChunk: number
  date: number
  step: UploadedFileStepType
  setupContent: MultipartUploadedFileSetupType | undefined
  initialSetupContent: MultipartUploadedFileSetupType | undefined
  convertTaskId?: string
  convertProgress?: number
  createdEntity?: CubeInfo
  origin: UploadedFileOriginType
  entityType: UploadedFileEntityType
  mappedEntities?: MappedEntityType[]
  // Флаг обновления существующих в проекте сущностей при обнаружении дублей
  updateIfExists?: boolean
}

export type SegySetupType = {
  surveyName: string
  surveyType: SurveyType
  domainType: CommonDomainType
  ignoreSegy: boolean
  check: boolean
  ignoreZeros: boolean
  headerCoords?: SegyHeaderCoordsType
  samples?: SegySampleParamsInfo
  isValid?: boolean
}

export type SegyHeaderCoordsType = {
  x?: { offset?: number; type?: SegyHeaderType }
  y?: { offset?: number; type?: SegyHeaderType }
  iline?: { offset?: number; type?: SegyHeaderType }
  xline?: { offset?: number; type?: SegyHeaderType }
}

export type SegyHeaderType =
  | 'INT_2_BYTES'
  | 'INT_4_BYTES'
  | 'IBM_FLOATING_POINT'
  | 'IEEE_FLOATING_POINT'

export type SegySampleParamsInfo = {
  samplesFormat?: SegyHeaderType
  samplesPerTrace?: number
  interval?: number
  startRecord?: number
  scale?: number
}

type BaseProgressNotificationInfo = {
  entityId?: string
  taskId?: string
  progress: number
}

export type LoadingProgressNotificationInfo = BaseProgressNotificationInfo & {
  partNumber: number
}

export type ConvertProgressNotificationInfo = BaseProgressNotificationInfo & {
  taskId: string
  status: 'Failed' | 'Progress' | 'Done' | 'Running' | 'Pending'
  message: string
}

export type CubeAddedToProjectNotificationInfo = {
  cubeId: CubeId
  projectId: ProjectId
}

export const defaultUploadedFileStepStatuses: { [key in UploadedFileStepType]: 'excluded' } = {
  setup: 'excluded',
  mapping: 'excluded',
  loading: 'excluded',
  conversion: 'excluded',
  ready: 'excluded',
}

export const columnSeparators = ['space', 'comma', 'tab', 'colon', 'semicolon', 'slash'] as const
export type ColumnSeparator = (typeof columnSeparators)[number]

export type FileEncoding = 'cp1251' | 'utf8' | 'utf16'

type FileParseSettings = {
  encoding: FileEncoding
  isValid?: boolean
}

export type TabularFormattedFileParseSettings = FileParseSettings & {
  missedLinesCount: number
  separator: ColumnSeparator
}

export type MarkerSetupType = TabularFormattedFileParseSettings & {
  timeZone: string
  units: string
  negativeZ: boolean
  negativeTWT: boolean
  markerNameColumn: number
  markerTypeColumn?: number
  wellNameColumn: number
  markerBoreDepthColumn?: number
  markerAbsDepthColumn?: number
}

type MappingStatus = 'success' | 'error' | 'manual' | 'notmapped'

export type MappedEntityType = {
  name: string
  status: MappingStatus
  mappedTo?: WellListItem
}
